import React from 'react';
import { Box, styled } from '@mui/material';
import { useNavigate } from 'react-router';
import { useSelector } from 'react-redux';
import { ContainerLayout, HeaderBar, ScrollableContainer } from 'components/index';
import { useFeatureFlags } from 'features/DeveloperTools/hooks/useFeatureFlags';
import { FeatureFlagKeys } from 'features/DeveloperTools/config/constants';
import { useTranslationContext } from 'contexts/TranslationContext/TranslationContext';
import { selectUser } from 'features/auth/authSlice';
import { useBiometrics } from 'hooks/useBiometrics';
import { useBloodGlucoseDatapoints } from 'hooks/useBloodGlucoseDatapoints';
import { useProgressStats } from 'hooks/useProgressStats';
// eslint-disable-next-line max-len
import { ChecklistCompletionWidgetView } from 'components/ProgressTab/Widgets/ChecklistCompletionWidget/ChecklistCompletionWidget';
import { BloodSugarWidget } from 'components/ProgressTab/Widgets/BloodSugarWidget/BloodSugarWidget';
import { MedicationWidgetView } from 'components/ProgressTab/Widgets/MedicationWidget/MedicationWidget';
// eslint-disable-next-line max-len
import { WeightProgressWidget } from 'components/ProgressTab/Widgets/ChecklistCompletionWidget/WeightProgressWidget/WeightProgressWidget';
import { StepCountWidgetView } from 'components/ProgressTab/Widgets/StepCountWidget/StepCountWidget';
import { formatLocalDateString } from 'utils';
import { BloodPressureWidget } from 'components/BloodPressureWidget/BloodPressureWidget';
import { HealthTrackerEmptyState } from 'components/HealthTrackerEmptyState/HealthTrackerEmptyState';
import { HealthTrackerSelectionModal } from 'components/HealthTrackerSelection/HealthTrackerSelectionModal';
import { useGetCareProgramRequestsQuery } from 'services/careProgramRequestService';
import { CareProgramRequestStatus } from 'features/resources/types';

const WidgetContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),
  boxSizing: 'border-box',
  width: '100%',
  maxWidth: 766,
  margin: '0 auto',
  padding: theme.spacing(2),
  [theme.breakpoints.down('sm')]: {
    paddingLeft: theme.spacing(4),
    paddingRight: theme.spacing(4),
  },
}));

export const Progress = () => {
  const navigate = useNavigate();
  const { translation } = useTranslationContext();
  const { isEnabled } = useFeatureFlags();
  const isProgressFeatureEnabled = isEnabled(FeatureFlagKeys.PROGRESS_FEATURE);
  const { medicationStats, physicalActivityStats, taskStats, isLoading, isError } =
    useProgressStats();
  const [isTrackerSelectionOpen, setIsTrackerSelectionOpen] = React.useState(false);

  const user = useSelector(selectUser);
  const { startDate, endDate } = React.useMemo(() => {
    const end = new Date();
    const start = new Date();
    start.setDate(end.getDate() - 6);
    return { startDate: formatLocalDateString(start), endDate: formatLocalDateString(end) };
  }, []);

  const { weight, bloodPressure } = useBiometrics(user?.id, { startDate, endDate });
  const bloodGlucose = useBloodGlucoseDatapoints(user?.id);
  const hasBloodPressureData =
    bloodPressure.data != null &&
    bloodPressure.data.data != null &&
    bloodPressure.data.data.length > 0;

  const memberUuid = user?.uuid ?? '';
  const { data: careProgramRequests } = useGetCareProgramRequestsQuery(
    { memberUuid },
    { skip: !memberUuid },
  );
  const requestedBloodPressureTracker = React.useMemo(() => {
    const request = careProgramRequests?.find(
      ({ status, care_program: careProgram }) =>
        status === CareProgramRequestStatus.REQUESTED &&
        careProgram?.care_program_type === 'Health Tracker' &&
        careProgram.care_program_mappings?.biometric_type === 'Blood Pressure',
    );
    if (!request?.care_program) return undefined;
    return {
      programName:
        request.care_program.care_program_mappings.member_facing_name ||
        request.care_program.display_title,
      requestedOn: request.requested_on,
    };
  }, [careProgramRequests]);

  const requestedBloodSugarTracker = React.useMemo(() => {
    const request = careProgramRequests?.find(
      ({ status, care_program: careProgram }) =>
        status === CareProgramRequestStatus.REQUESTED &&
        careProgram?.care_program_type === 'Health Tracker' &&
        careProgram.care_program_mappings?.biometric_type === 'Blood Glucose',
    );
    if (!request?.care_program) return undefined;
    return {
      programName:
        request.care_program.care_program_mappings.member_facing_name ||
        request.care_program.display_title,
      requestedOn: request.requested_on,
    };
  }, [careProgramRequests]);

  const requestedWeightTracker = React.useMemo(() => {
    const request = careProgramRequests?.find(
      ({ status, care_program: careProgram }) =>
        status === CareProgramRequestStatus.REQUESTED &&
        careProgram?.care_program_type === 'Health Tracker' &&
        careProgram.care_program_mappings?.biometric_type === 'weight',
    );
    if (!request?.care_program) return undefined;
    return {
      programName:
        request.care_program.care_program_mappings.member_facing_name ||
        request.care_program.display_title,
      requestedOn: request.requested_on,
    };
  }, [careProgramRequests]);

  const isEnrolledForBloodPressure = React.useMemo(() => {
    const request = careProgramRequests?.find(
      ({ status, care_program: careProgram }) =>
        status === CareProgramRequestStatus.APPROVED &&
        careProgram?.care_program_type === 'Health Tracker' &&
        careProgram.care_program_mappings?.biometric_type === 'Blood Pressure',
    );
    return request != null;
  }, [careProgramRequests]);

  const isEnrolledForBloodSugar = React.useMemo(() => {
    const request = careProgramRequests?.find(
      ({ status, care_program: careProgram }) =>
        status === CareProgramRequestStatus.APPROVED &&
        careProgram?.care_program_type === 'Health Tracker' &&
        careProgram.care_program_mappings?.biometric_type === 'Blood Glucose',
    );
    return request != null;
  }, [careProgramRequests]);

  const hasBloodGlucoseType = user?.biometric_types?.includes('blood_glucose') ?? false;

  const isEnrolledForWeightTracking = React.useMemo(() => {
    const request = careProgramRequests?.find(
      ({ status, care_program: careProgram }) =>
        status === CareProgramRequestStatus.APPROVED &&
        careProgram?.care_program_type === 'Health Tracker' &&
        careProgram.care_program_mappings?.biometric_type?.toLowerCase() === 'weight',
    );
    return request != null;
  }, [careProgramRequests]);

  return (
    <ContainerLayout>
      <ScrollableContainer>
        {isProgressFeatureEnabled ? (
          <HeaderBar titleId="progress_screen_title" heading={translation('progress_tab_text')} />
        ) : null}
        {(taskStats != null ||
          medicationStats != null ||
          physicalActivityStats != null ||
          isLoading ||
          isError) && (
          <WidgetContainer>
            {(taskStats != null || isLoading || isError) && (
              <ChecklistCompletionWidgetView
                taskStats={taskStats ?? []}
                isLoading={isLoading}
                isError={isError && taskStats == null}
              />
            )}
            {(medicationStats != null || isLoading || isError) && (
              <MedicationWidgetView
                medicationStats={medicationStats ?? []}
                isLoading={isLoading}
                // RTK Query keeps the last data when a refetch fails; only show the error with nothing cached.
                isError={isError && medicationStats == null}
              />
            )}
            {(physicalActivityStats != null || isLoading || isError) && (
              <StepCountWidgetView
                physicalActivityStats={physicalActivityStats ?? []}
                isLoading={isLoading}
                isError={isError && physicalActivityStats == null}
              />
            )}
          </WidgetContainer>
        )}
        {(requestedBloodSugarTracker || isEnrolledForBloodSugar || hasBloodGlucoseType) && (
          <WidgetContainer>
            <BloodSugarWidget
              datapoints={bloodGlucose.datapoints}
              isLoading={bloodGlucose.isLoading}
              isError={bloodGlucose.isError}
              onClick={() => navigate('/progress/blood-sugar')}
            />
          </WidgetContainer>
        )}
        {(requestedBloodPressureTracker || isEnrolledForBloodPressure) && (
          <WidgetContainer>
            <BloodPressureWidget
              data={bloodPressure.data}
              isLoading={bloodPressure.isLoading}
              isError={bloodPressure.isError}
              requestedTracker={requestedBloodPressureTracker}
              onClick={() => navigate('/progress/blood-pressure')}
            />
          </WidgetContainer>
        )}
        <WidgetContainer>
          {(requestedWeightTracker || isEnrolledForWeightTracking) && (
            <WeightProgressWidget
              onClick={() => navigate('/progress/weight')}
              requestedTracker={requestedWeightTracker}
            />
          )}
        </WidgetContainer>
        <WidgetContainer>
          <HealthTrackerEmptyState onBrowseTrackers={() => setIsTrackerSelectionOpen(true)} />
        </WidgetContainer>
        <HealthTrackerSelectionModal
          open={isTrackerSelectionOpen}
          onClose={() => setIsTrackerSelectionOpen(false)}
          // TODO(WFMOB-16315): populate from the care-programs endpoint and wire
          // onSelectTracker to the care program enrollment flow.
          programs={[]}
        />
      </ScrollableContainer>
    </ContainerLayout>
  );
};
